import { FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { MasterLayout } from '../../_metronic/layout/MasterLayout';
import TopBarProgress from 'react-topbar-progress-indicator';
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper';
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils';
import { WithChildren } from '../../_metronic/helpers';
import { useUserAuthStore } from '../stores/userAuthStore';

const PrivateRoutes = () => {
  const { user } = useUserAuthStore();
  const VideosPage = lazy(() => import('../modules/content/video/VideosPage'));
  const CompilationsPage = lazy(
    () => import('../modules/content/manualCompilation/CompilationsPage'),
  );
  const DureelsPage = lazy(
    () => import('../modules/content/dureel/DureelsPage'),
  );
  const CollectionsPage = lazy(
    () => import('../modules/content/collection/CollectionsPage'),
  );
  const TaggingPage = lazy(
    () => import('../modules/content/tagging/TaggingPage'),
  );
  const SubtaggingPage = lazy(
    () => import('../modules/content/subtagging/SubtaggingPage'),
  );
  const HomeButtonPage = lazy(
    () => import('../modules/management/homeButton/TaggingPage'),
  );
  const OnboardingScreenPage = lazy(
    () => import('../modules/management/onboardingScreen/OnboardingScreenPage'),
  );
  const RecommenderRowPage = lazy(
    () => import('../modules/management/recommenderRow/RecommenderRowPage'),
  );
  const CharactersPage = lazy(
    () => import('../modules/featured/character/CharactersPage'),
  );
  const FeaturedVideoPage = lazy(
    () => import('../modules/featured/video/FeaturedVideosPage'),
  );
  const CategoryAgePage = lazy(
    () => import('../modules/management/categoryAge/CategoryAgePage'),
  );
  const PreRollPage = lazy(
    () => import('../modules/management/preRoll/PreRollPage'),
  );
  const PostRollPage = lazy(
    () => import('../modules/management/postRoll/PostRollPage'),
  );
  const GeoRegionPage = lazy(
    () => import('../modules/management/geoRegion/GeoRegionPage'),
  );
  const UiTextPage = lazy(
    () => import('../modules/management/uiText/UiTextPage'),
  );
  const PromoBannersPage = lazy(
    () => import('../modules/promo-banners/PromoBannersPage'),
  );
  const DuriImagesPage = lazy(
    () => import('../modules/duri-images/DuriImagesPage'),
  );
  const ExperimentsPage = lazy(
    () => import('../modules/experiments/ExperimentsPage'),
  );
  const AdsPage = lazy(() => import('../modules/ads/AdsPage'));
  const GamePage = lazy(() => import('../modules/management/game/GamePage'));
  const AvatarsPage = lazy(
    () => import('../modules/management/team/avatar/AvatarsPage'),
  );
  const TranscoderPage = lazy(
    () => import('../modules/management/transcoder/TranscoderPage'),
  );
  const CollectionTopsPage = lazy(
    () => import('../modules/management/collectionTop/VideosPage'),
  );
  const SubscribersPage = lazy(
    () => import('../modules/subscribers/subscriber/SubscribersPage'),
  );
  const ProfilesPage = lazy(
    () => import('../modules/subscribers/profiles/ProfilesPage'),
  );
  const GuestsPage = lazy(
    () => import('../modules/subscribers/guests/GuestsPage'),
  );
  const ReportsPage = lazy(() => import('../modules/report/ReportsPage'));
  const SettingsPage = lazy(
    () => import('../modules/management/setting/SettingsPage'),
  );
  const OutsideLinksPage = lazy(
    () => import('../modules/management/outsideLink/OutsideLinksPage'),
  );
  const ViewershipPage = lazy(
    () => import('../modules/analytics/viewerships/ViewershipPage'),
  );
  const FeaturedBannerPage = lazy(
    () => import('../modules/management/featuredBanner/FeaturedBannerPage'),
  );
  const DynamicBannerPage = lazy(
    () => import('../modules/management/dynamicBanner/DynamicBannerPage'),
  );
  const CampaignOverlayPage = lazy(
    () => import('../modules/management/campaignOverlay/CampaignOverlayPage'),
  );
  const FeaturedBannerOverlayPage = lazy(
    () =>
      import(
        '../modules/management/featuredBannerOverlay/FeaturedBannerOverlayPage'
      ),
  );
  const VideoThumbnailOverlayPage = lazy(
    () =>
      import(
        '../modules/management/videoThumbnailOverlay/VideoThumbnailOverlayPage'
      ),
  );
  const SpecialRowPage = lazy(
    () => import('../modules/management/specialRow/SpecialRowPage'),
  );
  const InAppMessagePage = lazy(
    () => import('../modules/management/inAppMessage/InAppMessagePage'),
  );
  const LicensorRevSharesPage = lazy(
    () =>
      import('../modules/licensors/licensor-rev-shares/LicensorRevSharesPage'),
  );
  const ReportMRRPage = lazy(
    () => import('../modules/management/reportMRR/ReportMRRPage'),
  );
  const TopNavigationPage = lazy(
    () => import('../modules/management/topNavigation/TopNavigationPage'),
  );
  const LabelPage = lazy(() => import('../modules/management/label/LabelPage'));
  const CategoryContentPage = lazy(
    () => import('../modules/management/categories/CategoryContentPage'),
  );

  if (user.role.toLowerCase() === 'content uploader') {
    return (
      <Routes>
        <Route element={<MasterLayout />}>
          {/* Redirect to Dashboard after success login/registartion */}
          <Route
            path="auth/*"
            element={<Navigate to="/content/video/video-list" />}
          />
          {/* Pages */}
          {/* <Route path="dashboard" element={<DashboardWrapper />} /> */}
          {/* Lazy Modules */}
          <Route
            path="content/video/*"
            element={
              <SuspensedView>
                <VideosPage />
              </SuspensedView>
            }
          />
          <Route
            path="content/collection/*"
            element={
              <SuspensedView>
                <CollectionsPage />
              </SuspensedView>
            }
          />
          {/* Page Not Found */}
          <Route path="*" element={<Navigate to="/error/404" />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route
          path="auth/*"
          element={
            <Navigate
              to={
                user.role == 'admin'
                  ? '/content/video/video-list'
                  : '/analytics/viewerships/overview'
              }
            />
          }
        />
        {/* Pages */}
        {/* <Route path="dashboard" element={<DashboardWrapper />} /> */}
        {/* Lazy Modules */}
        {(user.role == 'admin' || user.role == 'report') && (
          <>
            <Route
              path="reports/*"
              element={
                <SuspensedView>
                  <ReportsPage />
                </SuspensedView>
              }
            />
            <Route
              path="licensor-rev-shares-list/*"
              element={
                <SuspensedView>
                  <LicensorRevSharesPage />
                </SuspensedView>
              }
            />
            <Route
              path="content/video/*"
              element={
                <SuspensedView>
                  <VideosPage />
                </SuspensedView>
              }
            />
            <Route
              path="content/compilation/*"
              element={
                <SuspensedView>
                  <CompilationsPage />
                </SuspensedView>
              }
            />
            <Route
              path="content/dureel/*"
              element={
                <SuspensedView>
                  <DureelsPage />
                </SuspensedView>
              }
            />
            <Route
              path="content/collection/*"
              element={
                <SuspensedView>
                  <CollectionsPage />
                </SuspensedView>
              }
            />
            <Route
              path="content/tagging/*"
              element={
                <SuspensedView>
                  <TaggingPage />
                </SuspensedView>
              }
            />
            <Route
              path="content/subtagging/*"
              element={
                <SuspensedView>
                  <SubtaggingPage />
                </SuspensedView>
              }
            />
            <Route
              path="featured/character/*"
              element={
                <SuspensedView>
                  <CharactersPage />
                </SuspensedView>
              }
            />
            <Route
              path="featured/video/*"
              element={
                <SuspensedView>
                  <FeaturedVideoPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/outside-links/*"
              element={
                <SuspensedView>
                  <OutsideLinksPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/category-content/*"
              element={
                <SuspensedView>
                  <CategoryContentPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/settings/*"
              element={
                <SuspensedView>
                  <SettingsPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/category-age/*"
              element={
                <SuspensedView>
                  <CategoryAgePage />
                </SuspensedView>
              }
            />
            <Route
              path="management/pre-roll/*"
              element={
                <SuspensedView>
                  <PreRollPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/post-roll/*"
              element={
                <SuspensedView>
                  <PostRollPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/home-button/*"
              element={
                <SuspensedView>
                  <HomeButtonPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/onboarding-screen/*"
              element={
                <SuspensedView>
                  <OnboardingScreenPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/recommender-row*"
              element={
                <SuspensedView>
                  <RecommenderRowPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/transcoder/*"
              element={
                <SuspensedView>
                  <TranscoderPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/collection-top/*"
              element={
                <SuspensedView>
                  <CollectionTopsPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/featured-banner/*"
              element={
                <SuspensedView>
                  <FeaturedBannerPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/dynamic-banner/*"
              element={
                <SuspensedView>
                  <DynamicBannerPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/campaign-overlay/*"
              element={
                <SuspensedView>
                  <CampaignOverlayPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/featured-banner-overlay/*"
              element={
                <SuspensedView>
                  <FeaturedBannerOverlayPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/video-thumbnail-overlay/*"
              element={
                <SuspensedView>
                  <VideoThumbnailOverlayPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/special-row/*"
              element={
                <SuspensedView>
                  <SpecialRowPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/in-app-message/*"
              element={
                <SuspensedView>
                  <InAppMessagePage />
                </SuspensedView>
              }
            />
            <Route
              path="management/report-mrr/*"
              element={
                <SuspensedView>
                  <ReportMRRPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/top-navigation/*"
              element={
                <SuspensedView>
                  <TopNavigationPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/label/*"
              element={
                <SuspensedView>
                  <LabelPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/geo-region/*"
              element={
                <SuspensedView>
                  <GeoRegionPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/ui-text/*"
              element={
                <SuspensedView>
                  <UiTextPage />
                </SuspensedView>
              }
            />
            <Route
              path="management/game/*"
              element={
                <SuspensedView>
                  <GamePage />
                </SuspensedView>
              }
            />
            <Route
              path="management/team/avatar/*"
              element={
                <SuspensedView>
                  <AvatarsPage />
                </SuspensedView>
              }
            />
            <Route
              path="manage-growth/promo-banners/*"
              element={
                <SuspensedView>
                  <PromoBannersPage />
                </SuspensedView>
              }
            />
            <Route
              path="manage-growth/duri-images/*"
              element={
                <SuspensedView>
                  <DuriImagesPage />
                </SuspensedView>
              }
            />
            <Route
              path="ab-tests/*"
              element={
                <SuspensedView>
                  <ExperimentsPage />
                </SuspensedView>
              }
            />
            <Route
              path="video-ads/*"
              element={
                <SuspensedView>
                  <AdsPage />
                </SuspensedView>
              }
            />
          </>
        )}
        <Route
          path="subscribers/users/*"
          element={
            <SuspensedView>
              <SubscribersPage />
            </SuspensedView>
          }
        />
        <Route
          path="subscribers/profile/*"
          element={
            <SuspensedView>
              <ProfilesPage />
            </SuspensedView>
          }
        />
        <Route
          path="subscribers/guests/*"
          element={
            <SuspensedView>
              <GuestsPage />
            </SuspensedView>
          }
        />
        <Route
          path="analytics/viewerships/*"
          element={
            <SuspensedView>
              <ViewershipPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

export const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary');
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
